<template>
  <div class="billing-page">
    <div class="billing-plan-current" v-if="data">
      <div
        class="billing-plan-badge"
        :class="'badge-' + data.subscription.plan.slug"
      >
        {{ data.subscription.plan.name }}
      </div>
      <div class="billing-plan-info">
        <div class="billing-plan-price">
          <span class="price-amount">
            <template v-if="data.subscription.plan.monthly_price > 0">
              &euro;{{ data.subscription.plan.monthly_price }}
            </template>
            <template v-else>Free</template>
          </span>
          <span
            class="price-period"
            v-if="data.subscription.plan.monthly_price > 0"
          >
            /
            {{
              data.subscription.billing_cycle === "yearly" ? "year" : "month"
            }}
          </span>
        </div>
        <p class="billing-plan-desc">
          {{ data.subscription.plan.description }}
        </p>
      </div>
      <div class="billing-plan-meta">
        <div class="billing-meta-item" v-if="data.subscription.next_payment_at">
          <span class="meta-label">Next payment</span>
          <span class="meta-value">{{
            formatDate(data.subscription.next_payment_at)
          }}</span>
        </div>
        <div class="billing-meta-item" v-if="data.subscription.subscribed_at">
          <span class="meta-label">Subscribed</span>
          <span class="meta-value">{{
            formatDate(data.subscription.subscribed_at)
          }}</span>
        </div>
        <div class="billing-meta-item" v-if="data.subscription.trial_ends_at">
          <span class="meta-label">Trial ends</span>
          <span class="meta-value">{{
            formatDate(data.subscription.trial_ends_at)
          }}</span>
        </div>
        <div class="billing-meta-item">
          <span class="meta-label">Status</span>
          <span class="meta-value status" :class="data.subscription.status">{{
            data.subscription.status
          }}</span>
        </div>
      </div>
    </div>

    <div class="billing-section">
      <h3 class="billing-section-title">Usage & Limits</h3>
      <div class="row g-3" v-if="data">
        <div
          class="col-sm-6 col-lg-3"
          v-for="limit in usageLimits"
          :key="limit.key"
        >
          <div class="billing-limit-card">
            <div class="limit-header">
              <span class="limit-label">{{ limit.label }}</span>
              <span class="limit-count"
                ><template v-if="limit.format === 'bytes'">{{
                  formatBytes(limit.current)
                }}</template
                ><template v-else>{{ limit.current }}</template
                ><span v-if="limit.max > 0"
                  ><template v-if="limit.format === 'bytes'">
                    / {{ formatBytes(limit.max) }}</template
                  ><template v-else> / {{ limit.max }}</template></span
                ></span
              >
            </div>
            <div class="limit-bar-track" v-if="limit.max > 0">
              <div
                class="limit-bar-fill"
                :class="{
                  'limit-warning': limit.percent >= 80,
                  'limit-danger': limit.percent >= 95,
                }"
                :style="{ width: Math.min(limit.percent, 100) + '%' }"
              ></div>
            </div>
            <div class="limit-bar-track unlimited" v-else>
              <div class="limit-bar-fill unlimited"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="billing-section">
      <h3 class="billing-section-title">Payment Method</h3>
      <div class="billing-payment-card">
        <template v-if="data && data.subscription.payment_method_type">
          <div class="payment-info">
            <BaseIcon :name="paymentIcon" class="payment-icon" />
            <div>
              <div class="payment-type">
                {{ data.subscription.payment_method_type }}
              </div>
              <div
                class="payment-details"
                v-if="data.subscription.payment_method_details"
              >
                {{ data.subscription.payment_method_details }}
              </div>
            </div>
          </div>
          <button
            class="btn btn-outline-secondary btn-sm"
            @click="showPaymentForm = true"
          >
            Change
          </button>
        </template>
        <template v-else>
          <div class="payment-empty">
            <span>No payment method saved</span>
            <button
              class="btn btn-type-2 btn-sm"
              @click="showPaymentForm = true"
            >
              Add Payment Method
            </button>
          </div>
        </template>
      </div>

      <div class="billing-payment-form" v-if="showPaymentForm">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label bold-12 text-gray">Payment type</label>
            <v-select
              v-model="paymentForm.type"
              :options="paymentTypes"
              :reduce="(c) => c"
              label="label"
              placeholder="Select payment type"
              class="remove-padding"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label bold-12 text-gray">Details</label>
            <input
              v-model="paymentForm.details"
              type="text"
              class="form-control remove-padding"
              placeholder="Card ending in **** or PayPal email"
            />
          </div>
        </div>
        <div class="text-end mt-3">
          <button
            class="btn btn-type-4 btn-sm me-2"
            @click="showPaymentForm = false"
          >
            Cancel
          </button>
          <button class="btn btn-type-2 btn-sm" @click="savePaymentMethod">
            Save
          </button>
        </div>
      </div>
    </div>

    <div class="billing-section">
      <h3 class="billing-section-title">Change Plan</h3>
      <div class="row g-3">
        <div
          v-for="plan in availablePlans"
          :key="plan.id"
          class="col-sm-6 col-lg-3"
        >
          <div
            class="billing-plan-card"
            :class="{ current: isCurrentPlan(plan) }"
          >
            <div class="plan-card-header">
              <h4 class="plan-card-name">{{ plan.name }}</h4>
              <div class="plan-card-price">
                <template v-if="plan.monthly_price > 0">
                  <span class="price">&euro;{{ plan.monthly_price }}</span>
                  <span class="period">/mo</span>
                </template>
                <template v-else>
                  <span class="price">Free</span>
                </template>
              </div>
              <p class="plan-card-desc">{{ plan.description }}</p>
            </div>
            <ul class="plan-card-features">
              <li v-for="feature in plan.features || []" :key="feature">
                <BaseIcon name="check" /> {{ feature }}
              </li>
            </ul>
            <button
              v-if="!isCurrentPlan(plan)"
              class="btn btn-type-2 w-100"
              @click="changePlan(plan)"
              :disabled="changing === plan.id"
            >
              <span>
                {{
                  changing === plan.id ? "Switching..." : "Switch to " + plan.name
                }}
              </span>
            </button>
            <div v-else class="plan-card-current-badge"><span>Current plan</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../modules/http";
import date from "@/modules/date";
import BaseIcon from "../icons/BaseIcon";

export default {
  name: "Billing",
  components: { BaseIcon },
  data() {
    return {
      data: null,
      showPaymentForm: false,
      changing: null,
      paymentForm: {
        type: null,
        details: "",
      },
      paymentTypes: [
        { label: "Credit Card", value: "credit_card" },
        { label: "PayPal", value: "paypal" },
        { label: "Bank Transfer", value: "bank_transfer" },
        { label: "SEPA Direct Debit", value: "sepa" },
      ],
    };
  },
  computed: {
    availablePlans() {
      return this.data ? this.data.plans : [];
    },
    usageLimits() {
      if (!this.data) return [];
      const plan = this.data.subscription?.plan;
      const limits = plan?.limits || {};
      const usage = this.data.usage || {};
      const items = [
        {
          key: "products",
          label: "Products",
          current: usage.products || 0,
          max: limits.products || 0,
        },
        {
          key: "users",
          label: "Users",
          current: usage.users || 0,
          max: limits.users || 0,
        },
        {
          key: "warehouses",
          label: "Warehouses",
          current: usage.warehouses || 0,
          max: limits.warehouses || 0,
        },
        {
          key: "monthly_orders",
          label: "Monthly Orders",
          current: usage.monthly_orders || 0,
          max: limits.monthly_orders || 0,
        },
        {
          key: "storage",
          label: "Storage",
          current: usage.storage_bytes || 0,
          max: limits.storage || 0,
          format: "bytes",
        },
        {
          key: "sale_channels",
          label: "Sale Channels",
          current: usage.sale_channels || 0,
          max: limits.sale_channels || 0,
        },
      ];
      return items.map((item) => ({
        ...item,
        percent: item.max > 0 ? (item.current / item.max) * 100 : 0,
      }));
    },
    paymentIcon() {
      const type = this.data?.subscription?.payment_method_type;
      if (type === "credit_card") return "card";
      if (type === "paypal") return "paypal";
      if (type === "bank_transfer") return "bank";
      return "wallet";
    },
  },
  methods: {
    formatBytes(bytes) {
      if (!bytes || bytes === 0) return "0 B";
      const units = ["B", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (
        parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + " " + units[i]
      );
    },
    formatDate(val) {
      return date.formatFullName(val);
    },
    isCurrentPlan(plan) {
      return this.data?.subscription?.plan?.id === plan.id;
    },
    changePlan(plan) {
      this.changing = plan.id;
      http
        .fetch("/billing/change-plan", { plan_id: plan.id }, true, "POST")
        .then(() => this.load())
        .finally(() => (this.changing = null));
    },
    savePaymentMethod() {
      http
        .fetch(
          "/billing/payment-method",
          {
            payment_method_type:
              this.paymentForm.type?.value || this.paymentForm.type,
            payment_method_details: this.paymentForm.details,
          },
          true,
          "POST"
        )
        .then(() => {
          this.showPaymentForm = false;
          this.load();
        });
    },
    load() {
      http.fetch("/billing/current").then((data) => {
        this.data = data;
      });
    },
  },
  beforeMount() {
    this.load();
  },
};
</script>
